import {getHighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {UnderlinePanels} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {PreloadedQuery} from 'react-relay'
import {fetchQuery, graphql, useFragment, usePreloadedQuery, useQueryLoader, useRelayEnvironment} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import {IssueTimelineViewType} from '../../constants/timeline'
import type {TabbedTimelineConfig} from '../OptionConfig'
import type {TabbedTimelineFragment$key} from './__generated__/TabbedTimelineFragment.graphql'
import type {useActivityTimelineItemsBackFragment$key} from './__generated__/useActivityTimelineItemsBackFragment.graphql'
import type {useActivityTimelineItemsFrontFragment$key} from './__generated__/useActivityTimelineItemsFrontFragment.graphql'
import type {useActivityTimelineItemsQuery} from './__generated__/useActivityTimelineItemsQuery.graphql'
import type {useTimelineHighlightQuery} from './__generated__/useTimelineHighlightQuery.graphql'
import {ActivityTimelineSkeleton} from './ActivityTimelineSkeleton'
import type {EventProps} from './IssueTimelineItem'
import styles from './TabbedTimeline.module.css'
import {
  buildActivityTimelineConnectionIds,
  buildTabbedTimelineConnectionIds,
  type TimelineConnectionIds,
} from './TimelineConnectionContext'
import {TimelinePanel} from './TimelinePanel'
import {useActivityTimelineCount} from './use-activity-timeline-count'
import {activityTimelineQuery, useActivityTimelineItems} from './use-activity-timeline-items'
import {useConversationTimelineItems} from './use-conversation-timeline-items'
import {buildHighlightQueryVariables, highlightQuery} from './use-timeline-highlight'

type TimelineTab = 'conversation' | 'activity'

/**
 * Determines which tab a deeplinked event belongs to based on its prefix.
 * - `issuecomment` prefix → Conversation tab (comments)
 * - `event` prefix → Activity tab (non-comment events)
 * - No highlight → Conversation tab (default)
 */
function getDeeplinkTab(highlightedEvent: string | undefined): TimelineTab {
  const highlight = getHighlightedEvent(highlightedEvent)
  if (highlight?.prefix === 'event') return 'activity'
  return 'conversation'
}

type TabbedTimelineProps = {
  data: TabbedTimelineFragment$key
  highlightedEvent: string | undefined
  page?: number
  isTransferInProgress?: boolean
  showCopilotMenuItem?: boolean
  optionConfig: EventProps['optionConfig'] & TabbedTimelineConfig
} & Omit<EventProps, 'optionConfig'>

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const tabbedTimelineFragment = graphql`
  fragment TabbedTimelineFragment on Issue @argumentDefinitions(viewType: {type: "TimelineEventViewType"}) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useConversationTimelineItemsFrontFragment @arguments(viewType: $viewType)
    ...useConversationTimelineItemsBackFragment @arguments(viewType: $viewType)
  }
`

/**
 * Inner component that renders activity timeline items.
 * Split from ActivityPanelContent so hooks are never called conditionally.
 */
function ActivityPanelItems({
  node,
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  issueUrl,
  activityConnectionIds,
  highlightedEvent,
  page,
  isTransferInProgress,
  showCopilotMenuItem,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: {
  node: useActivityTimelineItemsFrontFragment$key & useActivityTimelineItemsBackFragment$key
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  issueUrl: string
  activityConnectionIds: TimelineConnectionIds
} & Omit<TabbedTimelineProps, 'data'>) {
  const {timelineItems, totalItemCount, highlightedReady} = useActivityTimelineItems({
    frontRef: node,
    backRef: node,
    issueId,
    highlightedEvent,
    page,
  })

  return (
    <TimelinePanel
      timelineItems={timelineItems}
      totalItemCount={totalItemCount}
      highlightedEvent={highlightedEvent}
      highlightedReady={highlightedReady}
      issueId={issueId}
      repositoryId={repositoryId}
      repositoryNameWithOwner={repositoryNameWithOwner}
      issueUrl={issueUrl}
      page={page}
      isTransferInProgress={isTransferInProgress}
      showCopilotMenuItem={showCopilotMenuItem}
      viewer={viewer}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
      liveUpdate={{
        viewType: 'ACTIVITY',
        connectionId: activityConnectionIds.back,
        frontConnectionId: activityConnectionIds.front,
      }}
    />
  )
}

/**
 * Renders the Activity tab content once data has been lazy-loaded.
 */
function ActivityPanelContent({
  queryRef,
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  issueUrl,
  activityConnectionIds,
  ...rest
}: {
  queryRef: PreloadedQuery<useActivityTimelineItemsQuery>
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  issueUrl: string
  activityConnectionIds: TimelineConnectionIds
} & Omit<TabbedTimelineProps, 'data'>) {
  const data = usePreloadedQuery<useActivityTimelineItemsQuery>(activityTimelineQuery, queryRef)

  const node = data.node
  if (!node) return null

  return (
    <ActivityPanelItems
      node={node}
      issueId={issueId}
      repositoryId={repositoryId}
      repositoryNameWithOwner={repositoryNameWithOwner}
      issueUrl={issueUrl}
      activityConnectionIds={activityConnectionIds}
      {...rest}
    />
  )
}

/**
 * Tabbed timeline view that splits events into "Conversation" and "Activity" tabs.
 *
 * The Conversation tab data is included in the initial page load query.
 * The Activity tab data is lazy-loaded on first tab click.
 *
 * Enabled when `tabbedTimeline` is `true` in the option config (driven by
 * the `issues_react_timeline_tabs` feature flag on the server side).
 */
export function TabbedTimeline({
  data,
  highlightedEvent,
  page,
  isTransferInProgress,
  showCopilotMenuItem,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: TabbedTimelineProps) {
  const effectivePage = page
  const {sendAnalyticsEvent} = useAnalytics()
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  // ─── Tab routing ───────────────────────────────────────────────────────
  // Deeplinked comments (#issuecomment-*) → Conversation tab
  // Deeplinked events (#event-*) → Activity tab

  // On initial page load (direct navigation / shared link), route to the tab
  // that owns the deeplinked event. Subsequent hash changes while on the page
  // (e.g. clicking a permalink) do NOT switch tabs so the user stays in context.
  const initialTab = useMemo(() => getDeeplinkTab(highlightedEvent), [highlightedEvent])
  const [activeTab, setActiveTab] = useState<TimelineTab>(initialTab)

  const isConversationActive = activeTab === 'conversation'
  const isActivityActive = activeTab === 'activity'

  // Pass the highlight to whichever tab is currently active.
  // On hard nav the initial tab is already set by prefix (getDeeplinkTab), so the
  // highlight reaches the correct tab. On soft nav (clicking a permalink while on
  // the page) the tab stays put and the highlight goes to the current tab.
  // Cross-tab leakage is prevented by isEventHighlighted() which validates the
  // prefix against the item typename.
  const conversationHighlightedEvent = isConversationActive ? highlightedEvent : undefined
  const activityHighlightedEvent = isActivityActive ? highlightedEvent : undefined

  const timelineData = useFragment<TabbedTimelineFragment$key>(tabbedTimelineFragment, data)

  const conversationConnectionIds = useMemo(() => buildTabbedTimelineConnectionIds(timelineData.id), [timelineData.id])
  const activityConnectionIds = useMemo(() => buildActivityTimelineConnectionIds(timelineData.id), [timelineData.id])

  // ─── Conversation tab ──────────────────────────────────────────────────

  const {timelineItems, totalItemCount, highlightedReady} = useConversationTimelineItems({
    timelineData,
    highlightedEvent: conversationHighlightedEvent,
    page: effectivePage,
  })

  // ─── Activity count (tertiary query) ───────────────────────────────────
  // Loaded as a lightweight tertiary query after the primary query resolves.
  // Fires on mount and subscribes for real-time updates, keeping the
  // Activity tab badge in sync without blocking the initial page load.
  const activityCount = useActivityTimelineCount(timelineData.id)

  // ─── Activity tab (lazy-loaded) ────────────────────────────────────────

  const [activityQueryRef, loadActivityQuery] = useQueryLoader<useActivityTimelineItemsQuery>(activityTimelineQuery)
  const environment = useRelayEnvironment()

  // When loading the activity tab for an event deeplink, preload the
  // highlight query in parallel so the highlighted item is already in the
  // Relay store when useTimelineHighlights runs (avoids a sequential fetch).
  const preloadHighlightForActivity = useCallback(() => {
    const highlight = getHighlightedEvent(activityHighlightedEvent)
    if (!highlight) return

    const variables = buildHighlightQueryVariables(timelineData.id, highlight, IssueTimelineViewType.ACTIVITY)
    fetchQuery<useTimelineHighlightQuery>(environment, highlightQuery, variables).subscribe({})
  }, [activityHighlightedEvent, timelineData.id, environment])

  const loadActivityData = useCallback(() => {
    if (!activityQueryRef) {
      loadActivityQuery({issueId: timelineData.id})
      preloadHighlightForActivity()
    }
  }, [activityQueryRef, loadActivityQuery, timelineData.id, preloadHighlightForActivity])

  // Eagerly load activity data when deeplinked to the activity tab
  useEffect(() => {
    if (isActivityActive) {
      loadActivityData()
    }
  }, [isActivityActive, loadActivityData])

  // ─── Analytics ─────────────────────────────────────────────────────────
  // Fire a single impression for whichever tab is initially visible so we
  // can measure the ratio of users who view Conversation vs. those who
  // also click Activity. When deeplinked to #event-*, initialTab is
  // 'activity' so we must not count that as a conversation view.
  const impressionSentRef = useRef(false)
  useEffect(() => {
    if (!impressionSentRef.current) {
      impressionSentRef.current = true
      sendAnalyticsEvent('analytics.view', 'ISSUE_TIMELINE_TABS', {
        tab: initialTab,
      })
    }
  }, [sendAnalyticsEvent, initialTab])

  const handleConversationTabSelect = useCallback(() => {
    setActiveTab('conversation')
    sendClickAnalyticsEvent({
      category: 'ISSUE_TIMELINE_TABS',
      action: 'select_tab',
      tab: 'conversation',
      initial_tab: initialTab,
    })
  }, [initialTab, sendClickAnalyticsEvent])

  const handleActivityTabSelect = useCallback(() => {
    setActiveTab('activity')
    loadActivityData()
    sendClickAnalyticsEvent({
      category: 'ISSUE_TIMELINE_TABS',
      action: 'select_tab',
      tab: 'activity',
      initial_tab: initialTab,
    })
  }, [initialTab, loadActivityData, sendClickAnalyticsEvent])

  const {isSticky, sentinelRef} = useStickyObserver()

  const activityCountLoading = activityCount === undefined

  return (
    <>
      <div ref={sentinelRef} className={styles.sentinel} />
      <UnderlinePanels
        aria-label="Timeline"
        className={clsx(styles.tabbedNav, isSticky && styles.stuck)}
        data-testid={TEST_IDS.tabbedTimelineNav}
        loadingCounters={activityCountLoading}
      >
        <UnderlinePanels.Tab
          aria-selected={isConversationActive}
          onSelect={handleConversationTabSelect}
          counter={totalItemCount}
        >
          Conversation
        </UnderlinePanels.Tab>
        <UnderlinePanels.Tab
          aria-selected={isActivityActive}
          onSelect={handleActivityTabSelect}
          counter={activityCount ?? ''}
        >
          Activity
        </UnderlinePanels.Tab>

        <UnderlinePanels.Panel hidden={!isConversationActive} data-testid={TEST_IDS.tabbedTimelineConversationPanel}>
          <TimelinePanel
            timelineItems={timelineItems}
            totalItemCount={totalItemCount}
            highlightedEvent={conversationHighlightedEvent}
            highlightedReady={highlightedReady}
            issueId={timelineData.id}
            repositoryId={timelineData.repository.id}
            repositoryNameWithOwner={timelineData.repository.nameWithOwner}
            issueUrl={timelineData.url}
            page={effectivePage}
            isTransferInProgress={isTransferInProgress}
            showCopilotMenuItem={showCopilotMenuItem}
            viewer={viewer}
            onCommentChange={onCommentChange}
            onCommentReply={onCommentReply}
            onCommentEditCancel={onCommentEditCancel}
            optionConfig={optionConfig}
            liveUpdate={{
              viewType: 'CONVERSATION',
              connectionId: conversationConnectionIds.back,
              frontConnectionId: conversationConnectionIds.front,
            }}
          />
        </UnderlinePanels.Panel>

        <UnderlinePanels.Panel hidden={!isActivityActive} data-testid={TEST_IDS.tabbedTimelineActivityPanel}>
          {activityQueryRef ? (
            <Suspense fallback={<ActivityTimelineSkeleton />}>
              <ActivityPanelContent
                queryRef={activityQueryRef}
                issueId={timelineData.id}
                repositoryId={timelineData.repository.id}
                repositoryNameWithOwner={timelineData.repository.nameWithOwner}
                issueUrl={timelineData.url}
                activityConnectionIds={activityConnectionIds}
                highlightedEvent={activityHighlightedEvent}
                page={effectivePage}
                isTransferInProgress={isTransferInProgress}
                showCopilotMenuItem={showCopilotMenuItem}
                viewer={viewer}
                onCommentChange={onCommentChange}
                onCommentReply={onCommentReply}
                onCommentEditCancel={onCommentEditCancel}
                optionConfig={optionConfig}
              />
            </Suspense>
          ) : (
            <ActivityTimelineSkeleton />
          )}
        </UnderlinePanels.Panel>
      </UnderlinePanels>
    </>
  )
}
